import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { RoleService } from '../../../../core/services/role.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Role } from '../../../../core/models/role';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'em-role-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, ToastModule, TranslatePipe],
  templateUrl: './role-form.component.html',
  styleUrl: './role-form.component.scss',
  providers: [MessageService, RoleService],
})
export class RoleFormComponent {
  form!: FormGroup;
  generalErrorMessage: string = ''; 
  isEditMode: boolean = false;
  errorMessages: any = {
    required: {
      name: 'layout.configurations.roles.addRole.nameIsRequired',
      description: 'layout.configurations.roles.addRole.descriptionIsRequired',
    },
    pattern: {
      name: 'layout.configurations.roles.addRole.nameCannotBeNumeric',
      description: 'layout.configurations.roles.addRole.descriptionCannotBeNumeric',
    }
  };
  translations: any = {};

  constructor(
    private fb: FormBuilder,
    private roleService: RoleService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    const noNumericOnlyPattern = /^(?!\d+$).+$/;
    this.form = this.fb.group({
      id: [null],
      name: ['', [Validators.required, Validators.pattern(noNumericOnlyPattern)]],
      description: ['', [Validators.required, Validators.pattern(noNumericOnlyPattern)]],
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.populateForm(this.config.data.role);
    }
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.roles.addRole.successMessage.detail',
        'layout.configurations.roles.editRole.successMessage.detail',
        'layout.configurations.roles.addRole.successMessage.title',
        'layout.configurations.roles.editRole.successMessage.title',
        'layout.configurations.roles.addRole.nameIsRequired',
        'layout.configurations.roles.addRole.descriptionIsRequired',
        'layout.configurations.roles.addRole.nameCannotBeNumeric',
        'layout.configurations.roles.addRole.descriptionCannotBeNumeric'
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(role: Role) {
    this.form.patchValue({
      id: role.id,
      name: role.name,
      description: role.description,
    });
  }

  onSave() {
    this.generalErrorMessage = '';
    if (this.form.valid) {
      if (this.isEditMode) {
        this.updateRole();
      } else {
        this.addRole();
      }
    }
  }

  addRole() {
    this.roleService.createRole(this.form.value).subscribe({
      next: (role: any) => {
        this.ref.close(role);
      },
      error: (error: any) => {
        console.error('Error creating role:', error);
        const errorDetail = this.translations['layout.configurations.roles.addRole.errorMessage.detail'] || 'Role already exists';
        const errorTitle = this.translations['layout.configurations.roles.addRole.errorMessage.title'] || 'Error';
        
        this.messageService.add({
          severity: 'error',
          summary: errorTitle,
          detail: errorDetail
        });
        
        this.generalErrorMessage = errorDetail;
      }
    });
  }

  updateRole() {
    this.roleService.updateRole(this.form.value.id, this.form.value).subscribe({
      next: (role) => {
        this.ref.close(role);
      },
      error: (error: any) => {
        console.error('Error updating role:', error);
        
        const errorDetail = this.translations['layout.configurations.roles.editRole.errorMessage.detail'] || 'Role already exists';
        const errorTitle = this.translations['layout.configurations.roles.editRole.errorMessage.title'] || 'Error';
        
        this.messageService.add({
          severity: 'error',
          summary: errorTitle,
          detail: errorDetail
        });
        
        this.generalErrorMessage = errorDetail;
      }
    });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);
    if (control?.invalid && (control?.dirty || control?.touched)) {
      const errors = control.errors;
      if (errors) {
        const errorKey = Object.keys(errors)[0];
        return {
          invalid: true,
          message: this.errorMessages[errorKey][key],
        };
      }
    }
    return { invalid: false, message: '' };
  }
}